import React, { createContext, useContext, useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import { useAuth } from './AuthContext';

/**
 * Подключение к /ws + диспетчер сообщений по типу.
 *
 * - На каждом логине запрашиваем тикет /api/ws/ticket и подключаемся.
 * - При обрыве — переподключаемся с экспоненциальной задержкой (с обновлением тикета).
 * - Компоненты подписываются через useWsEvent('тип', handler).
 *
 * Сообщения от сервера — JSON-объекты с полем `type`, например:
 *   { type: 'hello', user: {...} }
 *   { type: 'onec.contractor.updated', contractor: {...} }
 */
const WsContext = createContext(null);

export function WsProvider({ children }) {
  const { user } = useAuth();
  // Map<type, Set<handler>>; ref, чтобы не перезаписывать на каждый рендер
  const handlersRef = useRef(new Map());
  const wsRef = useRef(null);

  useEffect(() => {
    if (!user) return;

    let aborted = false;
    let ws = null;
    let reconnectMs = 1000;
    let reconnectTimer = null;

    const dispatch = (msg) => {
      const set = handlersRef.current.get(msg?.type);
      if (!set) return;
      for (const h of set) {
        try { h(msg); } catch (e) { console.error('ws handler error', e); }
      }
    };

    const connect = async () => {
      if (aborted) return;
      try {
        const { ticket } = await api.get('/api/ws/ticket');
        if (aborted) return;

        const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const url = `${proto}//${window.location.host}/ws?ticket=${encodeURIComponent(ticket)}`;
        ws = new WebSocket(url);
        wsRef.current = ws;

        ws.addEventListener('open', () => {
          reconnectMs = 1000;  // успешно — сбрасываем backoff
        });
        ws.addEventListener('message', (e) => {
          try { dispatch(JSON.parse(e.data)); }
          catch (err) { console.warn('ws bad message', err); }
        });
        ws.addEventListener('close', () => {
          if (aborted) return;
          wsRef.current = null;
          reconnectTimer = setTimeout(connect, reconnectMs);
          reconnectMs = Math.min(reconnectMs * 2, 30_000);
        });
        ws.addEventListener('error', () => {
          // close сработает следом; здесь ничего не делаем
        });
      } catch (e) {
        if (aborted) return;
        // не получили тикет / нет сети — повторим
        reconnectTimer = setTimeout(connect, reconnectMs);
        reconnectMs = Math.min(reconnectMs * 2, 30_000);
      }
    };

    connect();

    return () => {
      aborted = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws && ws.readyState <= WebSocket.OPEN) {
        try { ws.close(); } catch {}
      }
      wsRef.current = null;
    };
  }, [user?.id]);

  const subscribe = (type, handler) => {
    let set = handlersRef.current.get(type);
    if (!set) { set = new Set(); handlersRef.current.set(type, set); }
    set.add(handler);
    return () => {
      set.delete(handler);
      if (set.size === 0) handlersRef.current.delete(type);
    };
  };

  return <WsContext.Provider value={{ subscribe }}>{children}</WsContext.Provider>;
}

/**
 * Подписаться на WS-событие. handler можно передавать без useCallback —
 * внутри держим актуальную ссылку через ref.
 */
export function useWsEvent(type, handler) {
  const ctx = useContext(WsContext);
  const handlerRef = useRef(handler);
  // обновляем ref на каждом рендере, чтобы внутрь подписки попадал актуальный handler
  useEffect(() => { handlerRef.current = handler; });

  useEffect(() => {
    if (!ctx) return;
    const stable = (msg) => handlerRef.current?.(msg);
    return ctx.subscribe(type, stable);
  }, [ctx, type]);
}
